import styled from '@emotion/styled'
import { Edit } from '@vivaldis/icons'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Col,
  DatePicker,
  Form,
  Image,
  Row,
  Typography
} from '@vivaldis/antd-ui'
import { Divider } from '../../../../../../../Divider'
import { Step, useStep } from '../../../../../../provider/StepProvider'
import { useForm } from '../../../../provider/FormProvider'
import { CountryType } from '../../../../../../types/CountryType'

interface Props {
  countries: CountryType[]
}

export const WorkPermit = ({ countries }: Props) => {
  const [t] = useTranslation('web_ui')

  const { setStep } = useStep()
  const handleAddWorkPermitButtonClick = useCallback(() => {
    setStep(Step.AddWorkPermitStep)
  }, [setStep])

  const { values } = useForm()

  //  we show WORK PERMIT when user selected non-EEA country
  const isEeaMember = useMemo(() => {
    if (!values.nationalityCountryId) {
      return true
    }

    return !!countries.find(
      country => country.id === values.nationalityCountryId
    )?.eeaMember
  }, [countries, values.nationalityCountryId])

  const workPermitHasAnyValue = useMemo(() => {
    return (
      values.workPermitFrontPicture ||
      values.workPermitBackPicture ||
      values.workPermitExpiresOn
    )
  }, [
    values.workPermitBackPicture,
    values.workPermitExpiresOn,
    values.workPermitFrontPicture
  ])

  if (!values.nationalityCountryId) {
    return null
  }

  if (isEeaMember) {
    return null
  }

  return (
    <>
      <Divider sectionSeparator />
      <Base>
        <Typography.Title level={5} marginBottom={16}>
          {t(
            'edit_employee_personal_information_form.steps.enter_ssn.work_permit_section_header'
          )}
        </Typography.Title>
        {workPermitHasAnyValue ? null : (
          <Button
            onClick={handleAddWorkPermitButtonClick}
            type="default"
            size="middle"
            icon={<Edit />}
            disabled={false}
          >
            {t(
              'edit_employee_personal_information_form.steps.enter_ssn.add_work_permit_button'
            )}
          </Button>
        )}
      </Base>
      {workPermitHasAnyValue ? (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="workPermitFrontPicture"
                required
                label={t(
                  'edit_employee_personal_information_form.steps.enter_ssn.work_permit_front_picture.label'
                )}
              >
                <Image
                  width={250}
                  height={160}
                  src={String(values.workPermitFrontPicture)}
                  preview={false}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="workPermitBackPicture"
                required
                label={t(
                  'edit_employee_personal_information_form.steps.enter_ssn.work_permit_back_picture.label'
                )}
              >
                <Image
                  width={250}
                  height={160}
                  src={String(values.workPermitBackPicture)}
                  preview={false}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="workPermitExpiresOn"
            required
            label={t(
              'edit_employee_personal_information_form.steps.enter_ssn.work_permit_expires_on.label'
            )}
          >
            <DatePicker
              name="workPermitExpiresOn"
              format="dd-MM-yyyy"
              placeholder={t('form.required')}
              style={{ width: '100%' }}
              autoComplete="off"
              disabled
            />
          </Form.Item>
        </>
      ) : null}
    </>
  )
}

const Base = styled('div')`
  display: flex;
  justify-content: space-between;
`
